import {
  Combobox,
  type ComboboxOptionType,
  FormField,
  FormHelp,
  Icon,
  Label,
  VisuallyHidden,
} from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';
import { Fragment } from 'react';
import { IsVisible } from '@zg-rentals/react-hooks';
import { SkeletonListItem } from '../PropertyDropdown/Skeleton';

export const filterSpecialCharacters = (str: string, replaceWith = '') =>
  str.trim().replace(/[^a-zA-Z0-9- ]/g, replaceWith);

export const MAX_LENGTH_SEARCH_TEXT = 20;
export const ERROR_MESSAGE = 'Sorry, there was an error. Please try again.';
export const MAX_CHARACTERS_MESSAGE = 'You’ve reached the maximum characters';

export type SearchResultItem = {
  meta: string;
  value: string;
  icon?: React.ReactNode;
  label: string;
  listingAlias: string;
  conversationId: string;
  folder: string;
};

type SearchBarProps = {
  fetchMoreSearchResults: (searchText: string, offset: number) => void;
  hasError: boolean;
  hasMoreSearchResults: boolean;
  helperText: string;
  isDisabled: boolean;
  label: string;
  onBlur?: () => void;
  onChangeInputText: (inputText: string) => void;
  onFocus?: () => void;
  onSelectSearchItem: (selectedSearchResultItem: SearchResultItem) => void;
  placeholder: string;
  renderEmptyState?: () => null | JSX.Element;
  searchResults: Array<SearchResultItem>;
  searchText: string;
} & React.HTMLAttributes<HTMLDivElement>;

export default function SearchBar({
  fetchMoreSearchResults,
  hasError,
  hasMoreSearchResults,
  helperText,
  isDisabled,
  label,
  onBlur,
  onChangeInputText,
  onFocus,
  onSelectSearchItem,
  placeholder,
  renderEmptyState = () => null,
  searchResults,
  searchText,
  ...rest
}: SearchBarProps) {
  const handleScroll = (isLastSearchResultVisible: boolean) => {
    if (isLastSearchResultVisible && hasMoreSearchResults) {
      const offset = searchResults.length;
      fetchMoreSearchResults(searchText, offset);
    }
  };

  const handleComboboxSelect = (option: ComboboxOptionType) => {
    if (typeof option === 'object' && option !== null) {
      onSelectSearchItem(option as SearchResultItem);
    }
  };

  return (
    <FormField
      {...rest}
      error={hasError}
      description={helperText && <FormHelp>{helperText}</FormHelp>}
      label={
        <VisuallyHidden asChild>
          <Label>{label}</Label>
        </VisuallyHidden>
      }
      css={{
        // oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 47px` falls between `1100` (44px) and `1200` (48px) on the numeric sizes scale
        height: '47px',
      }}
      control={
        <Combobox
          css={{
            boxShadowColor: 'border.subtle',
          }}
          appearance="select"
          fluidDropdown
          focusFirstOption
          freeForm
          onChange={onChangeInputText}
          onOptionSelect={handleComboboxSelect}
          options={searchResults}
          placeholder={placeholder}
          selectCallbackFrequency="change"
          shouldAwaitInteractionResponse
          showLabelForValue
          size="md"
          value={searchText}
          renderEmptyState={renderEmptyState}
          renderInput={({ ref, ...rest }) => {
            return (
              <Combobox.Input
                ref={typeof ref === 'string' ? undefined : ref}
                {...rest}
                autoComplete="off"
                disabled={isDisabled}
                onFocus={onFocus}
                onBlur={onBlur}
                maxLength={MAX_LENGTH_SEARCH_TEXT}
              />
            );
          }}
          renderAdornment={({ ref, ...rest }) => (
            <Combobox.Adornment ref={typeof ref === 'string' ? undefined : ref} {...rest}>
              <Icon render={<IconSearchFilled />} />
            </Combobox.Adornment>
          )}
          renderOption={({
            option,
            highlightPattern,
            key,
            ref,
            renderOptionLabel,
            renderOptionMeta,
            renderOptionMedia,
            ...rest
          }) => {
            if (typeof option !== 'object') return;
            const { value, icon, label, meta } = option;

            const isLastItemInList = value === searchResults[searchResults.length - 1].value;
            const showVisibilitySensor = isLastItemInList && hasMoreSearchResults;
            return (
              <Fragment key={key}>
                <Combobox.Option option={option} ref={typeof ref === 'string' ? undefined : ref} {...rest}>
                  {renderOptionLabel?.({ children: label, highlightPattern })}
                  {meta && renderOptionMeta?.({ children: meta, highlightPattern })}
                  {icon && renderOptionMedia?.({ children: icon })}
                </Combobox.Option>
                {showVisibilitySensor && (
                  <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px -200px 0px' }}>
                    {({ ref }) => <SkeletonListItem ref={ref} />}
                  </IsVisible>
                )}
              </Fragment>
            );
          }}
        />
      }
    />
  );
}
